// ══════════════════════════════════════════════════════════════════
//  ED CRM - Service de Notifications (núcleo/aplicação)
// ══════════════════════════════════════════════════════════════════
//  Leitura da caixa de entrada do usuário + preferências. Recebe o
//  repositório via porta (DI) — não conhece Supabase. As funções puras
//  (metadados, agrupamento, defaults) saem direto do módulo.
//
//  Quem CRIA notificação é o banco (Config/103). Aqui só se lê, marca
//  lido e configura.
// ══════════════════════════════════════════════════════════════════

import type {
  NotificationEntry,
  NotificationKindMeta,
  NotificationPrefs,
} from '@/types/notification';
import type {
  INotificationsRepository,
  ListNotificationsQuery,
  SavePrefsData,
} from '../ports/INotificationsRepository';

// ─── Funções puras ──────────────────────────────────────────────

// `icon` usa os nomes registrados no DynIcon (src/components/ui/DynIcon.tsx).
export const NOTIFICATION_KIND_META: Record<string, NotificationKindMeta> = {
  'activity.due':     { icon: 'clipboard', label: 'Atividade',          color: '#2563C8' },
  'activity.overdue': { icon: 'alert',     label: 'Atividade atrasada', color: '#DC2626' },
  'meeting.upcoming': { icon: 'calendar',  label: 'Reunião',            color: '#0891B2' },
};

export const getNotificationKindMeta = (kind: string): NotificationKindMeta =>
  NOTIFICATION_KIND_META[kind] || { icon: 'dot', label: kind, color: '#6B7280' };

/**
 * Defaults quando o usuário nunca configurou nada. Espelha os DEFAULTs da
 * tabela (Config/102) — mudou lá, muda aqui.
 */
export const DEFAULT_NOTIFICATION_PREFS: Omit<NotificationPrefs, 'user_id' | 'workspace_id'> = {
  channels: { in_app: true, browser: true, email: false, whatsapp: false },
  activity_lead_times: [0, 15],
  meeting_lead_times:  [60, 15],
  kinds: {
    'activity.due':     true,
    'activity.overdue': true,
    'meeting.upcoming': true,
  },
  quiet_hours: {},
};

/** Rótulo curto de uma antecedência em minutos. */
export const leadTimeLabel = (minutes: number): string => {
  if (minutes <= 0) return 'No horário';
  if (minutes < 60) return `${minutes} min`;
  if (minutes < 1440) {
    const h = minutes / 60;
    return `${Number.isInteger(h) ? h : h.toFixed(1)} h`;
  }
  const d = minutes / 1440;
  return `${Number.isInteger(d) ? d : d.toFixed(1)} dia${d > 1 ? 's' : ''}`;
};

/**
 * Agrupa a caixa de entrada em "novas" (não lidas) e "lidas", preservando a
 * ordem de chegada. Função pura — a UI só renderiza.
 */
export const splitByRead = (
  items: NotificationEntry[] = [],
): { unread: NotificationEntry[]; read: NotificationEntry[] } => {
  const unread: NotificationEntry[] = [];
  const read: NotificationEntry[] = [];
  for (const n of items) (n.read_at ? read : unread).push(n);
  return { unread, read };
};

// ─── Service ────────────────────────────────────────────────────
export class NotificationsService {
  constructor(private readonly repo: INotificationsRepository) {}

  listNotifications = (query: ListNotificationsQuery): Promise<NotificationEntry[]> => {
    if (!query?.userId) return Promise.resolve([]);
    return this.repo.listRecent({ deliveredOnly: true, limit: 30, ...query });
  };

  countUnreadNotifications = (userId: string | null | undefined): Promise<number> => {
    if (!userId) return Promise.resolve(0);
    return this.repo.countUnread(userId);
  };

  markNotificationsRead = async (ids: string[]): Promise<number> => {
    const clean = (ids || []).filter(Boolean);
    if (clean.length === 0) return 0;
    await this.repo.markRead(clean);
    return clean.length;
  };

  markAllNotificationsRead = async (userId: string | null | undefined): Promise<void> => {
    if (!userId) return;
    await this.repo.markAllRead(userId);
  };

  dismissNotification = async (id: string): Promise<void> => {
    if (!id) return;
    await this.repo.remove(id);
  };

  /** Nunca devolve null: sem linha no banco, entrega os defaults. */
  getNotificationPrefs = async (
    userId: string | null | undefined,
    workspaceId: string | null | undefined,
  ): Promise<NotificationPrefs> => {
    const fallback: NotificationPrefs = {
      user_id: userId || '',
      workspace_id: workspaceId || '',
      ...DEFAULT_NOTIFICATION_PREFS,
    };
    if (!userId || !workspaceId) return fallback;
    const found = await this.repo.getPrefs(userId, workspaceId);
    return found || fallback;
  };

  saveNotificationPrefs = (data: SavePrefsData): Promise<NotificationPrefs> => {
    if (!data?.userId)      throw new Error('userId e obrigatorio.');
    if (!data?.workspaceId) throw new Error('workspaceId e obrigatorio.');
    return this.repo.savePrefs(data);
  };
}
